import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  MenuItem,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import Logo from '../components/Logo';

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';

const Register = () => {
  const [searchParams] = useSearchParams();
  const inviteToken = (searchParams.get('customer_invite') || '').trim();
  const [invitePreview, setInvitePreview] = useState(null);
  const [formData, setFormData] = useState({
    email: '',
    phone_number: '',
    company_name: '',
    password: '',
    passwordConfirm: '',
    role: 'customer',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!inviteToken) {
      setInvitePreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/customers/profile/team-invite-preview/?token=${encodeURIComponent(inviteToken)}`,
        );
        const data = await res.json();
        if (!cancelled && data.valid) {
          setInvitePreview({ company_name: data.company_name, email: data.email });
          setFormData((f) => ({
            ...f,
            role: 'customer',
            email: f.email || data.email || '',
          }));
        } else if (!cancelled) {
          setInvitePreview({ error: data.error || 'Invalid invitation' });
        }
      } catch {
        if (!cancelled) setInvitePreview({ error: 'Could not load invitation' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.passwordConfirm) {
      setError('Passwords do not match');
      return;
    }

    if (!formData.phone_number.trim()) {
      setError('Phone number is required');
      return;
    }

    const isCustomerSignup = inviteToken || formData.role === 'customer';
    if (isCustomerSignup && !inviteToken && !formData.company_name.trim()) {
      setError('Company name is required');
      return;
    }

    if (RECAPTCHA_SITE_KEY && !recaptchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);
    const result = await register(
      formData.email,
      formData.password,
      formData.passwordConfirm,
      inviteToken ? 'customer' : formData.role,
      inviteToken || undefined,
      formData.phone_number.trim(),
      recaptchaToken || undefined,
      !inviteToken && formData.role === 'customer' ? formData.company_name.trim() : undefined,
    );
    setLoading(false);

    if (result.success) {
      navigate('/customer/dashboard');
    } else {
      recaptchaRef.current?.reset();
      setRecaptchaToken('');
      const err = result.error;
      if (typeof err === 'string') {
        setError(err);
      } else if (err && typeof err === 'object') {
        const parts = Object.entries(err).map(([k, v]) => {
          const msg = Array.isArray(v) ? v.join(' ') : String(v);
          return `${k}: ${msg}`;
        });
        setError(parts.join(' ') || 'Registration failed');
      } else {
        setError('Registration failed');
      }
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: { xs: 3, sm: 5 },
            borderRadius: 2,
            background: '#ffffff',
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mb: 4,
            }}
          >
            <Logo size="medium" />
          </Box>

          <Typography
            component="h1"
            variant="h5"
            align="center"
            sx={{
              color: '#1E3A5F',
              fontWeight: 600,
              mb: 1,
            }}
          >
            Create Your Account
          </Typography>
          <Typography
            variant="body2"
            align="center"
            sx={{
              color: '#666',
              mb: 3,
            }}
          >
            Join us to get started with fuel delivery services
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 3,
                borderRadius: 1,
              }}
              onClose={() => setError('')}
            >
              {error}
            </Alert>
          )}

          {inviteToken && invitePreview?.company_name && (
            <Alert severity="info" sx={{ mb: 2 }}>
              You are joining <strong>{invitePreview.company_name}</strong>. Use the invited email address.
            </Alert>
          )}
          {inviteToken && invitePreview?.error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {invitePreview.error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#4A90E2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1E3A5F',
                  },
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              id="phone_number"
              label="Phone Number"
              name="phone_number"
              type="tel"
              autoComplete="tel"
              value={formData.phone_number}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#4A90E2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1E3A5F',
                  },
                },
              }}
            />
            {!inviteToken && (
              <TextField
                margin="normal"
                required
                fullWidth
                select
                id="role"
                label="Account Type"
                name="role"
                value={formData.role}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#4A90E2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1E3A5F',
                    },
                  },
                }}
              >
                <MenuItem value="customer">Customer</MenuItem>
                <MenuItem value="driver">Driver</MenuItem>
                <MenuItem value="manager">Manager</MenuItem>
              </TextField>
            )}
            {!inviteToken && formData.role === 'customer' && (
              <TextField
                margin="normal"
                required
                fullWidth
                id="company_name"
                label="Company Name"
                name="company_name"
                autoComplete="organization"
                value={formData.company_name}
                onChange={handleChange}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#4A90E2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1E3A5F',
                    },
                  },
                }}
              />
            )}
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#4A90E2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1E3A5F',
                  },
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="passwordConfirm"
              label="Confirm Password"
              type="password"
              id="passwordConfirm"
              value={formData.passwordConfirm}
              onChange={handleChange}
              sx={{
                '& .MuiOutlinedInput-root': {
                  '&:hover fieldset': {
                    borderColor: '#4A90E2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1E3A5F',
                  },
                },
              }}
            />
            {RECAPTCHA_SITE_KEY && (
              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={RECAPTCHA_SITE_KEY}
                  onChange={(token) => setRecaptchaToken(token || '')}
                  onExpired={() => setRecaptchaToken('')}
                />
              </Box>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || (RECAPTCHA_SITE_KEY && !recaptchaToken)}
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: '#1E3A5F',
                color: '#ffffff',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  background: '#2A4A6F',
                },
              }}
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                variant="text"
                onClick={() => navigate('/login')}
                sx={{
                  color: '#4A90E2',
                  textTransform: 'none',
                  fontWeight: 500,
                  '&:hover': {
                    background: 'rgba(74, 144, 226, 0.08)',
                  },
                }}
              >
                Already have an account? Sign In
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Footer */}
        <Box
          sx={{
            textAlign: 'center',
            mt: 3,
            color: '#666',
          }}
        >
          <Typography variant="body2">
            © 2026 DND LLC. All Rights Reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Register;
